import { useEffect, useMemo, useState } from "react";
import {
  Button, Card, Empty, Form, Input, Modal, Select, Space, Switch,
  Table, Tabs, Tag, Typography, message,
} from "antd";
import {
  ClipboardList, Copy, Edit3, Eye, FileText, PenLine, Plus,
  Search, Shield, Trash2,
} from "lucide-react";
import axiosClient from "../../api/axiosClient";

const { Title, Text } = Typography;

type FieldType = "text" | "textarea" | "select" | "checkbox" | "signature" | "date" | "heading" | "divider";
type FormCategory = "intake" | "waiver" | "consent" | "custom";
type Trigger = "all" | "new_clients" | "specific_service" | "recurring";

interface FormField {
  id: string;
  type: FieldType;
  label: string;
  required: boolean;
  options?: string[];
}

interface FormDef {
  form_id: string;
  name: string;
  category: FormCategory;
  description: string;
  trigger_type: Trigger;
  require_signature: boolean;
  active: boolean;
  fields: FormField[];
  completions: number;
  updated_at: string;
}

interface Submission {
  submission_id: string;
  form_id: string;
  form_name: string;
  form_category: FormCategory;
  client_name: string;
  status: string;
  submitted_at: string;
}

interface FormSettings {
  auto_send: boolean;
  reminders: boolean;
  staff_notify: boolean;
  retention_months: number;
}

const CATEGORY_META: Record<FormCategory, { label: string; color: string; icon: React.ReactNode }> = {
  intake: { label: "Intake Form", color: "blue", icon: <ClipboardList size={12} /> },
  waiver: { label: "Waiver", color: "red", icon: <Shield size={12} /> },
  consent: { label: "Consent Form", color: "purple", icon: <PenLine size={12} /> },
  custom: { label: "Custom Form", color: "default", icon: <FileText size={12} /> },
};

const TRIGGER_LABELS: Record<Trigger, string> = {
  all: "All bookings",
  new_clients: "New clients only",
  specific_service: "Specific service",
  recurring: "Every 12 months",
};

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkboxes" },
  { value: "signature", label: "Signature" },
  { value: "date", label: "Date Picker" },
  { value: "heading", label: "Section Heading" },
  { value: "divider", label: "Divider" },
];

let _nextFieldId = 1;
const uid = () => `field-${Date.now()}-${_nextFieldId++}`;

// ── Form Builder Modal ──────────────────────────────────────────────────────
function FormBuilderModal({
  initial,
  onSave,
  onClose,
}: {
  initial: Partial<FormDef> | null;
  onSave: (vals: { name: string; category: FormCategory; description: string; trigger_type: Trigger; require_signature: boolean; fields: FormField[] }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState<FormCategory>(initial?.category ?? "custom");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [trigger, setTrigger] = useState<Trigger>(initial?.trigger_type ?? "all");
  const [requireSignature, setRequireSignature] = useState(initial?.require_signature ?? false);
  const [fields, setFields] = useState<FormField[]>(initial?.fields ?? []);
  const [addingType, setAddingType] = useState<FieldType | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [newRequired, setNewRequired] = useState(false);

  const addField = () => {
    if (!addingType || !newLabel.trim()) return;
    const field: FormField = {
      id: uid(),
      type: addingType,
      label: newLabel.trim(),
      required: newRequired,
      ...(addingType === "select" || addingType === "checkbox" ? { options: ["Option 1", "Option 2"] } : {}),
    };
    setFields((f) => [...f, field]);
    setNewLabel("");
    setNewRequired(false);
    setAddingType(null);
  };

  const removeField = (id: string) => setFields((f) => f.filter((x) => x.id !== id));
  const moveUp = (idx: number) => {
    if (idx === 0) return;
    setFields((f) => { const a = [...f]; [a[idx - 1], a[idx]] = [a[idx], a[idx - 1]]; return a; });
  };
  const moveDown = (idx: number) => {
    setFields((f) => { if (idx === f.length - 1) return f; const a = [...f]; [a[idx], a[idx + 1]] = [a[idx + 1], a[idx]]; return a; });
  };

  return (
    <Modal
      title={initial?.form_id ? "Edit Form" : "New Form"}
      open
      onCancel={onClose}
      onOk={() => name.trim() && onSave({ name: name.trim(), category, description, trigger_type: trigger, require_signature: requireSignature, fields })}
      okText={initial?.form_id ? "Save Changes" : "Create Form"}
      okButtonProps={{ disabled: !name.trim() }}
      width={640}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
        <div style={{ gridColumn: "span 2" }}>
          <Text style={{ fontSize: 12, fontWeight: 600 }}>Form Name *</Text>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. New Client Intake Form" style={{ marginTop: 4 }} />
        </div>
        <div>
          <Text style={{ fontSize: 12, fontWeight: 600 }}>Category</Text>
          <Select
            value={category}
            onChange={setCategory}
            style={{ width: "100%", marginTop: 4 }}
            options={(Object.keys(CATEGORY_META) as FormCategory[]).map((c) => ({ value: c, label: CATEGORY_META[c].label }))}
          />
        </div>
        <div>
          <Text style={{ fontSize: 12, fontWeight: 600 }}>Show For</Text>
          <Select
            value={trigger}
            onChange={setTrigger}
            style={{ width: "100%", marginTop: 4 }}
            options={(Object.entries(TRIGGER_LABELS) as [Trigger, string][]).map(([k, v]) => ({ value: k, label: v }))}
          />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <Text style={{ fontSize: 12, fontWeight: 600 }}>Description</Text>
          <Input.TextArea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Brief description for your team" style={{ marginTop: 4 }} />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 12, background: "#fafafa", borderRadius: 10, marginTop: 16 }}>
        <div>
          <Text style={{ fontSize: 13, fontWeight: 500 }}>Require Signature</Text>
          <div style={{ fontSize: 12, color: "#9ca3af" }}>Client must draw their signature to submit</div>
        </div>
        <Switch checked={requireSignature} onChange={setRequireSignature} />
      </div>

      <div style={{ marginTop: 16 }}>
        <Text style={{ fontSize: 13, fontWeight: 600 }}>Form Fields ({fields.length})</Text>
        {fields.length === 0 && (
          <div style={{ border: "2px dashed #e5e7eb", borderRadius: 10, padding: 20, textAlign: "center", color: "#9ca3af", fontSize: 13, marginTop: 8 }}>
            No fields yet — add one below
          </div>
        )}
        <Space direction="vertical" size={6} style={{ width: "100%", marginTop: 8 }}>
          {fields.map((field, idx) => (
            <div key={field.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "#fafafa", borderRadius: 8, padding: "8px 12px", border: "1px solid #f0f0f0" }}>
              <Tag>{FIELD_TYPES.find((t) => t.value === field.type)?.label}</Tag>
              <span style={{ flex: 1, fontSize: 13 }}>{field.label}</span>
              {field.required && <Tag color="red">Required</Tag>}
              <Button size="small" type="text" disabled={idx === 0} onClick={() => moveUp(idx)}>↑</Button>
              <Button size="small" type="text" disabled={idx === fields.length - 1} onClick={() => moveDown(idx)}>↓</Button>
              <Button size="small" type="text" danger icon={<Trash2 size={13} />} onClick={() => removeField(field.id)} />
            </div>
          ))}
        </Space>

        <div style={{ marginTop: 12, padding: 12, background: "#eff6ff", borderRadius: 10, border: "1px solid #dbeafe" }}>
          <Text style={{ fontSize: 11, fontWeight: 600, color: "#1d4ed8", textTransform: "uppercase" }}>Add Field</Text>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {FIELD_TYPES.map((ft) => (
              <Button key={ft.value} size="small" type={addingType === ft.value ? "primary" : "default"} onClick={() => setAddingType(ft.value)}>
                {ft.label}
              </Button>
            ))}
          </div>
          {addingType && (
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Field label"
                onPressEnter={addField}
                style={{ flex: 1 }}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} />
                Required
              </label>
              <Button type="primary" size="small" onClick={addField}>Add</Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── Preview Modal ────────────────────────────────────────────────────────────
function FormPreviewModal({ form, onClose }: { form: FormDef; onClose: () => void }) {
  return (
    <Modal title={form.name} open onCancel={onClose} footer={null} width={560}>
      <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 8 }}>
        {form.fields.map((field) => {
          if (field.type === "divider") return <div key={field.id} style={{ borderTop: "1px solid #f0f0f0" }} />;
          if (field.type === "heading") return <Text key={field.id} strong>{field.label}</Text>;
          return (
            <div key={field.id}>
              <Text style={{ fontSize: 13 }}>{field.label}{field.required && <span style={{ color: "#ef4444" }}> *</span>}</Text>
              <div style={{ marginTop: 4 }}>
                {field.type === "text" && <Input disabled />}
                {field.type === "textarea" && <Input.TextArea disabled rows={3} />}
                {field.type === "date" && <Input disabled placeholder="Date" />}
                {field.type === "select" && <Select disabled style={{ width: "100%" }} placeholder="Select an option…" options={field.options?.map((o) => ({ value: o, label: o }))} />}
                {field.type === "checkbox" && (
                  <Space direction="vertical" size={4}>
                    {field.options?.map((o) => (
                      <label key={o} style={{ fontSize: 13, color: "#6b7280" }}><input type="checkbox" disabled /> {o}</label>
                    ))}
                  </Space>
                )}
                {field.type === "signature" && (
                  <div style={{ border: "2px dashed #e5e7eb", borderRadius: 10, height: 60, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 12 }}>
                    Signature capture area
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </Space>
    </Modal>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function AdminDigitalFormsPage() {
  const [forms, setForms] = useState<FormDef[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [settings, setSettings] = useState<FormSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<FormCategory | "all">("all");
  const [previewForm, setPreviewForm] = useState<FormDef | null>(null);
  const [builderForm, setBuilderForm] = useState<Partial<FormDef> | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  function fetchAll() {
    setLoading(true);
    Promise.all([
      axiosClient.get("/forms"),
      axiosClient.get("/forms/submissions"),
      axiosClient.get("/forms/settings"),
    ])
      .then(([formsRes, subsRes, settingsRes]) => {
        setForms(formsRes.data ?? []);
        setSubmissions(subsRes.data ?? []);
        setSettings(settingsRes.data);
      })
      .catch(() => message.error("Failed to load forms"))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchAll(); }, []);

  async function toggleActive(form: FormDef) {
    try {
      await axiosClient.put(`/forms/${form.form_id}`, { ...form, active: !form.active });
      setForms((f) => f.map((x) => (x.form_id === form.form_id ? { ...x, active: !x.active } : x)));
    } catch {
      message.error("Failed to update form");
    }
  }

  async function handleSaveForm(vals: { name: string; category: FormCategory; description: string; trigger_type: Trigger; require_signature: boolean; fields: FormField[] }) {
    try {
      if (builderForm?.form_id) {
        await axiosClient.put(`/forms/${builderForm.form_id}`, { ...builderForm, ...vals, active: builderForm.active ?? true });
        message.success("Form updated successfully.");
      } else {
        await axiosClient.post("/forms", { ...vals, active: true });
        message.success("Form created successfully.");
      }
      setBuilderForm(null);
      fetchAll();
    } catch {
      message.error("Failed to save form");
    }
  }

  async function duplicateForm(form: FormDef) {
    try {
      await axiosClient.post("/forms", {
        name: `${form.name} (Copy)`,
        category: form.category,
        description: form.description,
        trigger_type: form.trigger_type,
        require_signature: form.require_signature,
        fields: form.fields,
        active: true,
      });
      message.success("Form duplicated.");
      fetchAll();
    } catch {
      message.error("Failed to duplicate form");
    }
  }

  function deleteForm(form: FormDef) {
    Modal.confirm({
      title: "Delete Form?",
      content: "This action cannot be undone. Submission history will be preserved.",
      okType: "danger",
      okText: "Delete",
      onOk: async () => {
        try {
          await axiosClient.delete(`/forms/${form.form_id}`);
          message.success("Form deleted.");
          fetchAll();
        } catch {
          message.error("Failed to delete form");
        }
      },
    });
  }

  async function saveSettings() {
    if (!settings) return;
    setSavingSettings(true);
    try {
      await axiosClient.put("/forms/settings", settings);
      message.success("Settings saved.");
    } catch {
      message.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  }

  const filteredForms = useMemo(() => forms.filter((f) => {
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase()) || f.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === "all" || f.category === categoryFilter;
    return matchSearch && matchCat;
  }), [forms, search, categoryFilter]);

  const waiverForms = forms.filter((f) => f.category === "waiver" || f.category === "consent");
  const activeCount = forms.filter((f) => f.active).length;
  const totalCompletions = forms.reduce((a, f) => a + f.completions, 0);

  const libraryTab = (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Input
          placeholder="Search forms…"
          prefix={<Search size={14} color="#9ca3af" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <Space wrap>
          {(["all", "intake", "waiver", "consent", "custom"] as const).map((cat) => (
            <Button key={cat} size="small" type={categoryFilter === cat ? "primary" : "default"} shape="round" onClick={() => setCategoryFilter(cat)}>
              {cat === "all" ? "All Forms" : CATEGORY_META[cat].label}
            </Button>
          ))}
        </Space>
        <Button type="primary" icon={<Plus size={14} />} style={{ marginLeft: "auto" }} onClick={() => setBuilderForm({})}>
          New Form
        </Button>
      </div>

      {filteredForms.length === 0 ? (
        <Empty description="No forms match your filter" />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {filteredForms.map((form) => (
            <Card key={form.form_id} size="small" style={{ opacity: form.active ? 1 : 0.7 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <Tag color={CATEGORY_META[form.category].color} icon={CATEGORY_META[form.category].icon}>
                  {CATEGORY_META[form.category].label}
                </Tag>
                <Switch size="small" checked={form.active} onChange={() => toggleActive(form)} />
              </div>
              <Text strong>{form.name}</Text>
              <p style={{ fontSize: 12, color: "#9ca3af", minHeight: 32, marginTop: 4 }}>{form.description || "No description"}</p>
              <div style={{ display: "flex", gap: 12, fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                <span>{form.fields.filter((f) => f.type !== "divider" && f.type !== "heading").length} fields</span>
                <span>{form.completions} completions</span>
              </div>
              <Text style={{ fontSize: 11, color: "#9ca3af" }}>{TRIGGER_LABELS[form.trigger_type]}</Text>
              {form.require_signature && <Tag color="purple" style={{ marginTop: 8 }}>Signature required</Tag>}
              <div style={{ display: "flex", gap: 4, marginTop: 12, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                <Button size="small" type="text" icon={<Eye size={13} />} onClick={() => setPreviewForm(form)} />
                <Button size="small" type="text" icon={<Edit3 size={13} />} onClick={() => setBuilderForm(form)} />
                <Button size="small" type="text" icon={<Copy size={13} />} onClick={() => duplicateForm(form)} />
                <Button size="small" type="text" danger icon={<Trash2 size={13} />} style={{ marginLeft: "auto" }} onClick={() => deleteForm(form)} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const waiversTab = (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: 12, fontSize: 12, color: "#92400e" }}>
        These templates are provided for convenience only and do not constitute legal advice. Have a qualified attorney review your waivers before use.
      </div>
      {waiverForms.map((form) => (
        <Card key={form.form_id} size="small">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Space>
                <Text strong>{form.name}</Text>
                <Tag color={CATEGORY_META[form.category].color}>{CATEGORY_META[form.category].label}</Tag>
                {!form.active && <Tag>Inactive</Tag>}
              </Space>
              <p style={{ fontSize: 12, color: "#9ca3af", margin: "4px 0 0" }}>{form.description}</p>
            </div>
            <Space>
              <Button size="small" icon={<Eye size={13} />} onClick={() => setPreviewForm(form)}>Preview</Button>
              <Button size="small" type="primary" icon={<Edit3 size={13} />} onClick={() => setBuilderForm(form)}>Edit</Button>
            </Space>
          </div>
        </Card>
      ))}
      {waiverForms.length === 0 && <Empty description="No waiver or consent forms yet" />}
    </Space>
  );

  const submissionsTab = (
    <Table
      rowKey="submission_id"
      loading={loading}
      dataSource={submissions}
      columns={[
        { title: "Client", dataIndex: "client_name" },
        {
          title: "Form",
          dataIndex: "form_name",
          render: (name: string, s: Submission) => <Space><Tag color={CATEGORY_META[s.form_category]?.color}>{CATEGORY_META[s.form_category]?.label}</Tag>{name}</Space>,
        },
        { title: "Submitted", dataIndex: "submitted_at", render: (v: string) => new Date(v).toLocaleString() },
        { title: "Status", dataIndex: "status", render: (v: string) => <Tag color={v === "complete" ? "green" : v === "partial" ? "gold" : "default"}>{v}</Tag> },
      ]}
      locale={{ emptyText: <Empty description="No submissions yet — this fills in once clients start completing forms" /> }}
    />
  );

  const settingsTab = settings && (
    <div style={{ maxWidth: 560 }}>
      <Card size="small">
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <Text style={{ fontWeight: 500 }}>Auto-Send Before Appointment</Text>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Automatically send forms to clients 24 hours before their appointment</div>
            </div>
            <Switch checked={settings.auto_send} onChange={(v) => setSettings({ ...settings, auto_send: v })} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <Text style={{ fontWeight: 500 }}>Completion Reminders</Text>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Send a reminder if a form hasn't been completed 2 hours before the appointment</div>
            </div>
            <Switch checked={settings.reminders} onChange={(v) => setSettings({ ...settings, reminders: v })} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <Text style={{ fontWeight: 500 }}>Staff Notifications</Text>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Notify the assigned stylist when a client completes their forms</div>
            </div>
            <Switch checked={settings.staff_notify} onChange={(v) => setSettings({ ...settings, staff_notify: v })} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Text style={{ fontWeight: 500 }}>Data Retention Period</Text>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>How long to keep completed form submissions</div>
            </div>
            <Select
              value={settings.retention_months}
              onChange={(v) => setSettings({ ...settings, retention_months: v })}
              style={{ width: 140 }}
              options={[
                { value: 12, label: "12 months" },
                { value: 24, label: "24 months" },
                { value: 36, label: "36 months" },
                { value: 60, label: "5 years" },
                { value: 0, label: "Forever" },
              ]}
            />
          </div>
        </Space>
      </Card>
      <Button type="primary" style={{ marginTop: 16 }} loading={savingSettings} onClick={saveSettings}>
        Save Settings
      </Button>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FileText size={18} color="#2563eb" />
          </div>
          <div>
            <Title level={3} style={{ margin: 0 }}>Digital Forms & Waivers</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>Paperless intake forms and liability waivers for your booking flow</Text>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16, maxWidth: 640 }}>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Active Forms</Text><div style={{ fontSize: 20, fontWeight: 700 }}>{activeCount}</div></Card>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Total Completions</Text><div style={{ fontSize: 20, fontWeight: 700 }}>{totalCompletions}</div></Card>
        <Card size="small"><Text type="secondary" style={{ fontSize: 12 }}>Submissions</Text><div style={{ fontSize: 20, fontWeight: 700 }}>{submissions.length}</div></Card>
      </div>

      <Tabs
        items={[
          { key: "library", label: "Forms Library", children: libraryTab },
          { key: "waivers", label: "Waivers", children: waiversTab },
          { key: "submissions", label: "Submissions", children: submissionsTab },
          { key: "settings", label: "Settings", children: settingsTab },
        ]}
      />

      {previewForm && <FormPreviewModal form={previewForm} onClose={() => setPreviewForm(null)} />}
      {builderForm !== null && (
        <FormBuilderModal initial={builderForm} onSave={handleSaveForm} onClose={() => setBuilderForm(null)} />
      )}
    </div>
  );
}
